import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { usePanel } from 'contexts/panelContext';
import Tools from 'components/userPanel/Tools';
import UserSettings from 'components/_common/UserSettings';
import PanelLayout from 'components/_layouts/panel/PanelLayout';
import { border, colors } from 'theme';
import { usersApi } from 'api';
import { MenuItemExt, PanelMenu } from 'components/_common/PanelMenu';
import { Pair } from 'types/Pair';
import { ProgramWithLanding } from 'types/Program';
import ReportIssues from 'components/_common/ReportIssues';
import ChangeOrganization from 'components/_common/panel/ChangeOrganization';
import { useScope } from 'contexts/scopeContext';
import { useGlobal } from 'contexts/globalContext';
import { useNavigate } from 'hooks/useNavigate';
import { useProgramTranslation } from 'hooks/useProgramTranslation';
import { Sidebar } from 'primereact/sidebar';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'components/_new/Button';
import { Divider } from 'components/_new/Divider';
import { useAuth } from 'contexts/authContext';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'components/_new/Dialog';
import { MyAccountDialog } from 'components/myAccount/MyAccountDialog';
import { MyCalendarDialog } from 'components/myCalendar/MyCalendarDialog';
import { ChangeAvatarDialog } from 'components/myAccount/components/ChangeAvatarDialog';
import { ChatButton } from 'components/chat/ChatButton';
import { surveyApi } from 'api/survey';
import { FinalSurveyPanel } from 'components/finalSurveyPage/FinalSurveyPanel';
import { userDisplayName } from 'utils/userDisplayName';
import { Icon, IconSvg } from 'components/_new/Icon';
import { KnowleedgeBaseContextProvider } from 'components/knowledgeBase/knowledgeBaseContext';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { GlobalSearch } from 'components/userPanel/components/GlobalSearch';
import i18n from 'locale';
import { ProgramFinishedPage } from '../ProgramFinishedPage';
import { WelcomeDialog } from './components/WelcomeDialog';
import { UserPanelBreadcrumbs, UserPanelBreadcrumbsProvider } from './components/UserPanelBreadcrumbs';

const PANEL_SCROLL_TARGETS = ['scrollWrapperRef', 'pageScrollWrapperRef'];
const PAIR_MENU_EXPANDED_STORAGE_KEY = 'userPanelExpandedPairMenu';

const getStoredExpandedPairMenu = (): Record<string, boolean> => {
	try {
		const value = window.localStorage.getItem(PAIR_MENU_EXPANDED_STORAGE_KEY);
		return value ? JSON.parse(value) : {};
	} catch {
		return {};
	}
};

const UserPanelLayoutInner = ({ currentProgram }: { currentProgram: ProgramWithLanding }) => {
	const { tProgram } = useProgramTranslation();
	const { organizationName, programName, pairId } = useParams();
	const initialPairIdRef = useRef<string | null>(pairId || null);
	const observedPairIdRef = useRef<string | null>(null);
	const [expandedPairMenu, setExpandedPairMenu] = useState<Record<string, boolean>>(getStoredExpandedPairMenu);
	const [autoExpandedPairMenu, setAutoExpandedPairMenu] = useState<Record<string, boolean>>({});
	const [pairMenuTouched, setPairMenuTouched] = useState(false);
	const navigate = useNavigate({
		scroll: PANEL_SCROLL_TARGETS,
	});

	const { currentUser, currentUserRefetch } = useAuth();
	const { panelType, canSwitchPanels, userPairs, panelScopePath, currentProgramMembership, todosData } = usePanel();
	const [showOrgDialog, setShowOrgDialog] = useState(false);
	const [visibleLeft, setVisibleLeft] = useState(false);
	const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
	const [globalSearchValue, setGlobalSearchValue] = useState('');
	const { hasPanelAccess } = useScope();
	const {
		toastRef,
		reportIssueModalIsOpen,
		setReportIssueModalIsOpen,
		myAccountModalOpen,
		setMyAccountModalOpen,
		myCalendarModalOpen,
		setMyCalendarModalOpen,
		avatarModalIsOpen,
		setAvatarModalIsOpen,
	} = useGlobal();

	useEffect(() => {
		if (!hasPanelAccess) {
			navigate('/');
			toastRef?.current?.show({
				severity: 'error',
				life: 3000,
				summary: tProgram('misc.error'),
				detail: 'Aplikacja wymaga akceptacji administratora programu',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasPanelAccess]);

	const hasStoredExpandedPairMenu = useMemo(() => Object.values(expandedPairMenu).some(Boolean), [expandedPairMenu]);
	const shouldUseStoredPairMenu = hasStoredExpandedPairMenu || pairMenuTouched;

	useEffect(() => {
		const normalizedPairId = /^\d+$/.test(pairId || '') ? pairId! : null;

		if (!normalizedPairId) {
			observedPairIdRef.current = null;
			return;
		}

		if (observedPairIdRef.current === normalizedPairId) {
			return;
		}

		observedPairIdRef.current = normalizedPairId;
		setPairMenuTouched(true);
		setAutoExpandedPairMenu((prev) => {
			if (prev[normalizedPairId]) {
				return prev;
			}

			return { ...prev, [normalizedPairId]: true };
		});
	}, [pairId]);

	const handlePairMenuExpandStateChange = useCallback((id: string, expanded: boolean) => {
		const matchedPairId = id.match(/^pair-(\d+)$/)?.[1];
		if (!matchedPairId) {
			return;
		}

		setPairMenuTouched(true);
		setAutoExpandedPairMenu((prev) => {
			if (!prev[matchedPairId]) {
				return prev;
			}

			const next = { ...prev };
			delete next[matchedPairId];
			return next;
		});
		setExpandedPairMenu((prev) => {
			const next = { ...prev };
			if (expanded) {
				next[matchedPairId] = true;
			} else {
				delete next[matchedPairId];
			}

			if (Object.keys(next).length > 0) {
				window.localStorage.setItem(PAIR_MENU_EXPANDED_STORAGE_KEY, JSON.stringify(next));
			} else {
				window.localStorage.removeItem(PAIR_MENU_EXPANDED_STORAGE_KEY);
			}

			return next;
		});
	}, []);

	const { displayName, logotype, pairingStatus } = currentProgram;
	const panelLabel = panelType === 'mentor' ? tProgram('misc.mentors') : tProgram('misc.mentees');
	const switchPanelLabel = panelType === 'mentor' ? tProgram('misc.mentees') : tProgram('misc.mentors');
	const switchPanelPath = `/panel/${organizationName}/${programName}/${panelType === 'mentor' ? 'mentee' : 'mentor'}`;

	const mapPairMenuItem = useCallback(
		(pair: Pair) => {
			const { menteeApplication, mentorApplication, id } = pair;

			const menteeUser = menteeApplication?.programMembership?.user || null;
			const mentorUser = mentorApplication?.programMembership?.user || null;

			const label = menteeUser ? userDisplayName(menteeUser) : userDisplayName(mentorUser);

			const isPairMenuExpanded = shouldUseStoredPairMenu
				? Boolean(expandedPairMenu[String(id)] || autoExpandedPairMenu[String(id)])
				: Boolean(initialPairIdRef.current && Number(initialPairIdRef.current) === id);

			const lastSessionId = pair?.stats?.lastSessionId || null;

			const evaluationLink = lastSessionId
				? `${panelScopePath}/pair/${id}/session/${lastSessionId}/evaluation-mentee`
				: null;
			const pairTodos =
				todosData.pair.find((pairTodo) => pairTodo.items.some((item) => item.variables?.pairId === id))
					?.items || [];

			const hasContractAlert = pairTodos.some((todo) => todo.type === 'contract_initial_sign_required');
			const hasMenteePlanAlert = pairTodos.some((todo) => todo.type === 'contract_goals_sign_required');
			const hasSessionAlert = pairTodos.some((todo) => todo.type === 'arrange_first_session_date');
			const hasFinalReportAlert = pairTodos.some((todo) => todo.type === `final_report_mentor`);
			const hasEvaluationAlert = pairTodos.some((todo) => todo.type === 'evaluation_survey_required');
			const hasProcessEndAlert = hasFinalReportAlert || hasEvaluationAlert;

			const processEndItems: MenuItemExt[] = [
				{
					id: `pair-${id}-final-report`,
					icon: <IconSvg name="checklist-square" />,
					label: tProgram('userPanel.navigation.finalReport'),
					badge: hasFinalReportAlert ? (
						<Icon
							name="exclamation-circle"
							iconStyle="solid"
							className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
						/>
					) : null,
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/final-report`),
				},
				evaluationLink
					? {
							id: `pair-${id}-evaluation`,
							icon: <IconSvg name="checklist" />,
							label: tProgram('userPanel.navigation.evaluation'),
							badge: hasEvaluationAlert ? (
								<Icon
									name="exclamation-circle"
									iconStyle="solid"
									className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
								/>
							) : null,
							command: () =>
								navigate({
									pathname: evaluationLink,
									hash: '#content',
								}),
						}
					: null,
				{
					id: `pair-${id}-process-summary`,
					icon: <IconSvg name="file-check" />,
					label: tProgram('userPanel.navigation.processSummary'),
					command: () =>
						navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/process-summary`),
				},
			].filter(Boolean) as MenuItemExt[];

			return {
				id: `pair-${id}`,
				label,
				expanded: isPairMenuExpanded,
				items: [
					{
						id: `pair-${id}-session`,
						icon: <IconSvg name="calendar-full" />,
						label: tProgram('userPanel.navigation.sessions'),
						command: () =>
							navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/session`),
						badge: hasSessionAlert ? (
							<Icon
								name="exclamation-circle"
								iconStyle="solid"
								className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
							/>
						) : null,
					},
					{
						id: `pair-${id}-contract`,
						icon: <IconSvg name="pencil-sign-square" />,
						label: tProgram('userPanel.navigation.contract'),
						badge: hasContractAlert ? (
							<Icon
								name="exclamation-circle"
								iconStyle="solid"
								className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
							/>
						) : null,
						command: () => {
							navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/contract`);
						},
					},
					{
						id: `pair-${id}-mentee-plan`,
						icon: <IconSvg name="text-write-square" />,
						label: tProgram('userPanel.navigation.mentoringPlan'),
						badge: hasMenteePlanAlert ? (
							<Icon
								name="exclamation-circle"
								iconStyle="solid"
								className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
							/>
						) : null,
						command: () =>
							navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/mentee-plan`),
					},
					{
						separator: true,
					},
					{
						id: `pair-${id}-mentors`,
						icon: <IconSvg name="identifier" />,
						label:
							pair.application.applicationRole === 'mentee'
								? tProgram('userPanel.navigation.pairDataForMentees')
								: tProgram('userPanel.navigation.pairDataForMentors'),
						command: () =>
							navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/pair-details`),
					},
					{
						id: `pair-${id}-materials`,
						icon: <IconSvg name="papers" />,
						label: tProgram('userPanel.navigation.userFiles'),
						command: () =>
							navigate(`/panel/${organizationName}/${programName}/${panelType}/pair/${id}/userfiles`),
					},
					{
						separator: true,
					},
					{
						id: `pair-${id}-process-end`,
						icon: <IconSvg name="checklist" />,
						label: tProgram('userPanel.navigation.processEnd'),
						flyout: true,
						badge: hasProcessEndAlert ? (
							<Icon
								name="exclamation-circle"
								iconStyle="solid"
								className="ml-2 fa fa-solid fa-triangle-exclamation text-rose"
							/>
						) : null,
						items: processEndItems,
					},
				].filter(Boolean) as MenuItemExt[],
			};
		},
		[
			expandedPairMenu,
			autoExpandedPairMenu,
			shouldUseStoredPairMenu,
			navigate,
			organizationName,
			programName,
			panelType,
			panelScopePath,
			todosData.pair,
			tProgram,
		]
	);

	const pairsMenu = useMemo(() => {
		if ((!userPairs || userPairs.length === 0) && !pairingStatus) {
			return [
				{
					id: 'no-pairs',
					label: tProgram('userPanel.navigation.noPairs'),
					disabled: true,
					icon: <Icon name="hourglass" iconStyle="regular" className="fa-xs pr-2" />,
					tooltip: tProgram('userPanel.navigation.noPairsTooltip'),
				},
			];
		}

		return (userPairs || []).map((pair) => mapPairMenuItem(pair));
	}, [userPairs, pairingStatus, mapPairMenuItem, tProgram]);

	const browseMembersMentorAccess = currentProgram.browseMembersByMentor && panelType === 'mentor';
	const browseMembersMenteeAccess = currentProgram.browseMembersByMentee && panelType === 'mentee';

	const linkedInBadgeEnabled =
		(currentProgram.mentorLabelKnowledgeBaseArticleId && panelType === 'mentor') ||
		(currentProgram.menteeLabelKnowledgeBaseArticleId && panelType === 'mentee');

	const { eventsEnabled } = currentProgram;

	const navigationOptions: MenuItemExt[] = useMemo(
		() =>
			[
				{
					id: 'dashboard',
					icon: <IconSvg name="home" />,
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}`),
					label: tProgram('userPanel.navigation.dashboard'),
					divider: true,
				},
				{
					id: 'divider1',
					className: 'p-panelmenu-divider',
				},
				{
					id: 'pairing',
					icon: <IconSvg name="users" />,
					label: tProgram('userPanel.navigation.pairing'),
					expanded: true,
					items: [
						...pairsMenu,
						currentProgram.pairingStatus && {
							id: 'yourPairs',
							command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/pairing`),
							label: tProgram('userPanel.navigation.yourPairs'),
						},
						// only if program has enabled option
						(browseMembersMentorAccess || browseMembersMenteeAccess) && {
							id: 'browseMembers',
							command: () =>
								navigate(`/panel/${organizationName}/${programName}/${panelType}/browseMembers`),
							label:
								panelType === 'mentee'
									? tProgram('userPanel.navigation.potentialMembers.allPairMentors')
									: tProgram('userPanel.navigation.potentialMembers.allPairMentees'),
						},
					].filter(Boolean),
				},
				{
					id: 'divider2',
					className: 'p-panelmenu-divider',
				},
				{
					id: 'knowledge-base',
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/knowledge-base`),
					label: `${tProgram('userPanel.navigation.files')}`,
					icon: <IconSvg name="book" />,
				},
				panelType === 'mentor' &&
					currentUser?.isProfessionalViewEnabled &&
					currentProgram.programType !== 'coaching' && {
						id: 'mentor-development',
						command: () => navigate(`${panelScopePath}/mentor-development`),
						label: tProgram('userPanel.navigation.mentorPlan'),
						icon: <IconSvg name="text-square" />,
					},
				eventsEnabled && {
					id: 'timeline',
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/timeline`),
					label: `${tProgram('userPanel.navigation.timeline')}`,
					icon: <IconSvg name="calendar-date" />,
				},
				{
					id: 'your-data',
					label: tProgram('userPanel.navigation.yourData'),
					icon: <IconSvg name="user" />,
					expanded: false,
					items: [
						{
							id: 'personal-details',
							command: () =>
								navigate(`/panel/${organizationName}/${programName}/${panelType}/personal-details`),
							label: tProgram('userPanel.navigation.personalDetails'),
							icon: <IconSvg name="user" />,
						},
						...(linkedInBadgeEnabled
							? [
									{
										id: 'linkedin-badge',
										command: () =>
											navigate(
												`/panel/${organizationName}/${programName}/${panelType}/linkedIn-badge`
											),
										label: tProgram('userPanel.navigation.linkedInBadge'),
										icon: <IconSvg name="brand-linkedin" />,
									},
								]
							: []),
						{
							id: 'edit-your-data',
							command: () =>
								navigate(`/panel/${organizationName}/${programName}/${panelType}/application`),
							label: tProgram('userPanel.personalDetails.editData'),
							icon: <IconSvg name="edit-square" />,
						},
						{
							id: 'my-calendar',
							command: () => {
								setMyCalendarModalOpen(true);
							},
							label: tProgram('userPanel.navigation.myCalendar'),
							icon: <IconSvg name="calendar-full" />,
						},
						{
							id: 'account-settings',
							command: () => setMyAccountModalOpen?.(true),
							label: tProgram('userPanel.navigation.accountSettings'),
							icon: <IconSvg name="settings" />,
						},
					],
				},
				(currentProgram as any).hasContact && {
					id: 'contact',
					command: () => navigate(`/panel/${organizationName}/${programName}/${panelType}/contact`),
					label: tProgram('userPanel.navigation.contact'),
					icon: <IconSvg name="communication" />,
				},
			].filter(Boolean),
		[
			navigate,
			tProgram,
			panelScopePath,
			currentUser?.isProfessionalViewEnabled,
			eventsEnabled,
			linkedInBadgeEnabled,
			setMyCalendarModalOpen,
			browseMembersMenteeAccess,
			browseMembersMentorAccess,
			currentProgram,
			organizationName,
			pairsMenu,
			panelType,
			programName,
		]
	);

	const navigationBottomOptions: MenuItemExt[] = useMemo(
		() => [
			{
				id: 'programPage',
				icon: <IconSvg name="desktop" />,
				label: tProgram('userPanel.navigation.programPage'),
				command: () => navigate(`/${organizationName}/${programName}`),
			},
			{
				id: 'reportIssue',
				icon: <IconSvg name="hand" />,
				label: tProgram('userPanel.navigation.reportIssue'),
				command: () => setReportIssueModalIsOpen?.(true),
			},
		],
		[navigate, organizationName, programName, setReportIssueModalIsOpen, tProgram]
	);

	const [surveyPopupDialogOpen, setSurveyPopupDialogOpen] = useState(false);
	const { search } = useLocation();
	const [suppressSurveyPopup, setSuppressSurveyPopup] = useState(
		new URLSearchParams(search).get('opensurvey') === '1'
	);

	useEffect(() => {
		if (new URLSearchParams(search).get('opensurvey') === '1') {
			setSuppressSurveyPopup(true);
		}
	}, [search]);

	useQuery(
		['FINAL_SURVEY_POPUP', { programMembershipId: Number(currentProgramMembership?.id) }],
		() => surveyApi.checkSurveyPopupForProgramMembership(Number(currentProgramMembership?.id)),
		{
			enabled: Boolean(currentProgramMembership?.id) && !suppressSurveyPopup,
			onSuccess: (displayPopup: boolean) => {
				if (displayPopup) {
					setSurveyPopupDialogOpen(true);
				}
			},
		}
	);

	const { pathname } = useLocation();
	const isPairRoute = pathname.includes('/pair/');
	const shouldCollapseMobileNavigation = !isPairRoute && !shouldUseStoredPairMenu && !initialPairIdRef.current;

	return (
		<>
			<Helmet title={tProgram('misc.pageTitles.panel')} />
			{currentProgram.programType !== 'coaching' && <WelcomeDialog />}
			<FinalSurveyPanel
				withIntro
				visible={surveyPopupDialogOpen}
				onHide={() => setSurveyPopupDialogOpen(false)}
			/>
			<Dialog
				title={tProgram('landingPages.buttons.changeOrganization')}
				size="sm"
				onHide={() => setShowOrgDialog(false)}
				visible={showOrgDialog}
			>
				<ChangeOrganization
					defaultSelected={
						organizationName && programName
							? {
									organization: organizationName,
									program: programName,
								}
							: undefined
					}
					card
					onCancel={() => setShowOrgDialog(false)}
				/>
			</Dialog>
			<Dialog
				title={tProgram('userPanel.navigation.reportIssue')}
				size="lg"
				onHide={() => setReportIssueModalIsOpen?.(false)}
				visible={Boolean(reportIssueModalIsOpen)}
			>
				<ReportIssues />
			</Dialog>

			<MyAccountDialog
				visible={myAccountModalOpen}
				onHide={() => {
					setMyAccountModalOpen(false);
				}}
			/>
			<ChangeAvatarDialog visible={avatarModalIsOpen} onHide={() => setAvatarModalIsOpen(false)} />
			<MyCalendarDialog
				visible={myCalendarModalOpen}
				onHide={() => {
					setMyCalendarModalOpen(false);
				}}
			/>

			<PanelLayout
				navigationOptions={navigationOptions}
				onNavigationExpandStateChange={handlePairMenuExpandStateChange}
				navigationTop={
					<div className="h-5rem p-2">
						<div className="flex flex-column align-items-end justify-content-center h-full w-full text-base text-right">
							{logotype && (
								<img
									src={logotype}
									alt="logo"
									className="max-h-3rem max-w-full mb-2 cursor-pointer align-self-end"
									onClick={() => navigate(`/${organizationName}/${programName}`)}
								/>
							)}
						</div>
					</div>
				}
				navigationBottom={<PanelMenu model={navigationBottomOptions} multiple />}
				header={
					<>
						<Button
							icon="fa fa-bars fa-2x"
							iconOnly
							label="Toggle menu"
							onClick={() => setVisibleLeft(true)}
							variant="primary-text"
							className="mx-2 flex lg:hidden"
						/>
						<Sidebar
							role="navigation"
							visible={visibleLeft}
							onHide={() => setVisibleLeft(false)}
							showCloseIcon={false}
							style={{
								backgroundColor: `${colors.purplishBlue}`,
								borderRadius: '0px 20px 20px 0px',
								maxWidth: '70%',
							}}
						>
							<PanelMenu
								model={navigationOptions.map((item) => ({
									...item,
									...(shouldCollapseMobileNavigation || item.id === 'your-data'
										? {
												expanded: false,
											}
										: {}),
								}))}
								onExpandStateChange={handlePairMenuExpandStateChange}
								onHide={() => setVisibleLeft(false)}
								multiple
							/>
							<Divider margin={4} />
							<PanelMenu model={navigationBottomOptions} onHide={() => setVisibleLeft(false)} multiple />
						</Sidebar>
						<div className="flex flex-1 align-items-center gap-2 min-w-0 md:hidden pr-2">
							<GlobalSearch
								className="flex-1 min-w-0"
								isOpen={globalSearchOpen}
								onOpenChange={setGlobalSearchOpen}
								searchValue={globalSearchValue}
								onSearchValueChange={setGlobalSearchValue}
							/>
						</div>
						<div className="hidden md:flex align-items-center flex-1 min-w-0">
							<div className="h-3rem pr-4 flex-shrink-0 px-2">
								<h3 className="mt-0 mb-1">{displayName}</h3>

								<div className="flex flex-column gap-0 text-sm">
									<span>
										{tProgram('userPanel.youAreInPanel')}{' '}
										{canSwitchPanels ? (
											<>
												<Tooltip target="#switchPanelLink" position="bottom" />
												<Link
													to={switchPanelPath}
													id="switchPanelLink"
													className="inline-flex align-items-center gap-1"
													style={{ color: colors.purplishBlue }}
													data-pr-tooltip={`${tProgram(
														'userPanel.goTo'
													)} ${switchPanelLabel}`}
												>
													<b>{panelLabel}</b>
													<Icon
														name="arrow-right-arrow-left"
														iconStyle="solid"
														className="text-xs"
													/>
												</Link>
											</>
										) : (
											<b>{panelLabel}</b>
										)}
									</span>
								</div>
							</div>
							<div className="flex-1 min-w-0 px-3" style={{ maxWidth: '600px' }}>
								<GlobalSearch
									className="w-full"
									isOpen={globalSearchOpen}
									onOpenChange={setGlobalSearchOpen}
									searchValue={globalSearchValue}
									onSearchValueChange={setGlobalSearchValue}
								/>
							</div>
						</div>
						{currentUser && (
							<div
								className="flex align-items-center h-3rem ml-auto pr-3 pl-3 gap-2 flex-shrink-0"
								style={{ borderLeft: border(colors.lightGray) }}
							>
								<div className="hidden md:flex align-items-center gap-2 mr-2">
									<span
										className="text-xs text-muted pr-1 professional-view-label"
										data-tooltip={
											currentUser?.isProfessionalViewEnabled
												? tProgram('userPanel.settings.professionalViewTooltipOn')
												: tProgram('userPanel.settings.professionalViewTooltipOff')
										}
									>
										{currentUser?.isProfessionalViewEnabled
											? tProgram('userPanel.settings.professionalView')
											: tProgram('userPanel.settings.simpleView')}
									</span>
									<InputSwitch
										checked={currentUser?.isProfessionalViewEnabled}
										onChange={() =>
											usersApi
												.editMyData({
													isProfessionalViewEnabled: !currentUser?.isProfessionalViewEnabled,
												})
												.then(() => currentUserRefetch())
										}
									/>
								</div>
								<ChatButton />
								<UserSettings
									currentUser={currentUser}
									setShowOrgDialog={setShowOrgDialog}
									optionsLanguage={i18n.resolvedLanguage}
									source="userPanel"
								/>
							</div>
						)}
					</>
				}
				right={<Tools />}
				contentClassName="user-panel-content"
			>
				<UserPanelBreadcrumbs />
				<div className="user-panel-route">
					<Outlet />
				</div>
			</PanelLayout>
		</>
	);
};

const UserPanelLayout = () => {
	const { currentProgram } = useScope();
	const { panelLinkPrefix, currentApplicationData } = usePanel();
	if (!currentProgram) {
		return null;
	}

	if (currentProgram.finished) {
		return <ProgramFinishedPage nextProgramName={currentProgram.nextProgramName} />;
	}

	const linkPrefix = `${panelLinkPrefix}/knowledge-base`;

	return (
		<KnowleedgeBaseContextProvider
			context="user-panel"
			useSlugReplacer
			linkPrefix={`/${linkPrefix}`}
			queries={{
				getDirectories: () => knowledgeBaseApi.application.getDirectories(currentApplicationData.id),
				getArticlesByDirectory: (directoryId: number, searchValue?: string | null) =>
					knowledgeBaseApi.application.getArticlesByDirectory(
						currentApplicationData.id,
						directoryId,
						searchValue
					),
				getArticlesByTag: (tagId: number, searchValue?: string | null) =>
					knowledgeBaseApi.application.getArticlesByTag(tagId, currentApplicationData.id, searchValue),
				getArticlesBySearch: (searchValue: string | null) =>
					knowledgeBaseApi.application.getArticlesBySearch(currentApplicationData.id, searchValue),
				getArticleById: (articleId: number, params) =>
					knowledgeBaseApi.application.getArticleById(currentApplicationData.id, articleId, params),
				getRecommendedArticles: () =>
					knowledgeBaseApi.application.getRecommendedArticles(currentApplicationData.id),
				getArticlesByLabelsOrIds: (labels: Array<string | number>) =>
					knowledgeBaseApi.application.getArticlesByLabelsOrIds(currentApplicationData.id, labels),
				getDirectoryByLabels: (labels: string[]) =>
					knowledgeBaseApi.application.getDirectoryByLabels(currentApplicationData.id, labels),
				getProgramOrganizersArticles: () =>
					knowledgeBaseApi.application.getProgramArticles(currentApplicationData.id),
			}}
		>
			<UserPanelBreadcrumbsProvider>
				<UserPanelLayoutInner currentProgram={currentProgram} />
			</UserPanelBreadcrumbsProvider>
		</KnowleedgeBaseContextProvider>
	);
};

export default UserPanelLayout;
